import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { formatApiError, safeNext, withNext } from "@/lib/api";
import { MAP_IMG } from "@/lib/constants";

export default function AuthPage({ mode }) {
  const isLogin = mode === "login";
  const { login, register } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const next = safeNext(sp.get("next"));
  const [f, setF] = useState({ name: "", email: "", password: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const googleLogin = () => {
    if (next) sessionStorage.setItem("lorely_next", next);
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/worlds";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      isLogin ? await login(f.email, f.password) : await register(f.name, f.email, f.password);
      nav(next || "/worlds");
    } catch (ex) {
      setErr(formatApiError(ex));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2" data-testid={`${mode}-page`}>
      <div className="relative hidden lg:block">
        <img src={MAP_IMG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-60" />
        <div className="absolute inset-0 bg-gradient-to-r from-transparent to-canvas" />
        <blockquote className="absolute bottom-14 left-14 max-w-md font-display italic text-3xl text-amber-50/90 leading-snug">
          "Not all those who wander are lost — but they should keep good notes."
        </blockquote>
      </div>
      <div className="relative z-10 flex items-center justify-center px-6 py-16">
        <form onSubmit={submit} className="w-full max-w-sm space-y-6 rise">
          <Link to="/" className="font-display text-2xl text-amber-100" data-testid="auth-brand-link">Lore<span className="text-amber-500">ly</span></Link>
          <div>
            <h1 className="font-display text-4xl sm:text-5xl text-amber-50">{isLogin ? "Welcome back" : "Open a new codex"}</h1>
            <p className="text-sm text-slate-400 mt-2">{isLogin ? "Your worlds have waited for you." : "Free, private, and yours alone."}</p>
          </div>
          {!isLogin && (
            <div className="space-y-1.5"><Label>Pen name</Label>
              <Input value={f.name} onChange={set("name")} placeholder="Ursula" data-testid="auth-name-input" /></div>
          )}
          <div className="space-y-1.5"><Label>Email</Label>
            <Input type="email" required value={f.email} onChange={set("email")} placeholder="you@story.com" data-testid="auth-email-input" /></div>
          <div className="space-y-1.5"><Label>Password</Label>
            <Input type="password" required value={f.password} onChange={set("password")} placeholder="At least 6 characters" data-testid="auth-password-input" /></div>
          {err && <p className="text-sm text-rose-400" data-testid="auth-error">{err}</p>}
          <Button type="submit" disabled={busy} className="w-full rounded-full h-11 bg-amber-500 hover:bg-amber-400 text-onaccent font-semibold" data-testid="auth-submit-btn">
            {busy ? "One moment..." : isLogin ? "Sign in" : "Create account"}
          </Button>
          <div className="flex items-center gap-3 text-xs text-slate-500"><span className="h-px flex-1 bg-line" /> or <span className="h-px flex-1 bg-line" /></div>
          <Button type="button" variant="outline" onClick={googleLogin} className="w-full rounded-full h-11 bg-transparent border-line hover:border-amber-500/40" data-testid="google-login-btn">
            <svg viewBox="0 0 24 24" className="w-4 h-4"><path fill="#EA4335" d="M12 10.2v3.9h5.5c-.2 1.4-1.7 4.1-5.5 4.1-3.3 0-6-2.7-6-6.1s2.7-6.1 6-6.1c1.9 0 3.1.8 3.8 1.5l2.6-2.5C16.8 3.4 14.6 2.4 12 2.4 6.7 2.4 2.4 6.7 2.4 12s4.3 9.6 9.6 9.6c5.5 0 9.2-3.9 9.2-9.4 0-.6-.1-1.1-.2-1.6H12z" /></svg>
            Continue with Google
          </Button>
          <p className="text-sm text-slate-400">
            {isLogin ? "New here? " : "Already a member? "}
            <Link to={withNext(isLogin ? "/register" : "/login", next)} className="text-amber-400 hover:underline" data-testid="auth-switch-link">
              {isLogin ? "Create an account" : "Sign in"}
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
}
